// Feedback button component for alpha users to submit feedback.

import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { authApi, AuthError } from '../api/auth';
import { useAuthStore } from '../stores/authStore';

const FEEDBACK_CATEGORIES = [
  'bug',
  'feature_request',
  'general',
  'ui_ux',
  'performance',
  'data_accuracy',
];

function currentPageContext(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

const FeedbackButton: React.FC = () => {
  const { isAuthenticated } = useAuthStore();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [category, setCategory] = useState('general');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (
    !isAuthenticated ||
    location.pathname === '/chat' ||
    location.pathname === '/market' ||
    location.pathname === '/feedback'
  ) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rating === 0) {
      setError('Please select a rating.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await authApi.submitFeedback({
        rating,
        category,
        comment: comment || undefined,
        page_url: currentPageContext(),
      });
      setIsSuccess(true);
      setTimeout(() => {
        setIsOpen(false);
        setRating(0);
        setCategory('general');
        setComment('');
        setIsSuccess(false);
      }, 2000);
    } catch (err) {
      if (err instanceof AuthError) {
        setError(err.message);
      } else {
        setError('Failed to submit feedback. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setRating(0);
    setCategory('general');
    setComment('');
    setError(null);
    setIsSuccess(false);
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-36 right-4 z-40 rounded-full bg-primary-600 p-4 text-white shadow-lg transition-all hover:scale-105 hover:bg-primary-700 md:bottom-6 md:right-6"
        aria-label="Submit feedback"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
          <div className="relative bg-glass-secondary rounded-xl shadow-2xl p-6 w-full max-w-md border border-glass-border">
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {isSuccess ? (
              <div className="text-center py-8">
                <div className="text-green-400 text-5xl mb-4">✓</div>
                <h3 className="text-xl font-semibold text-white mb-2">Thank You!</h3>
                <p className="text-gray-400">Your feedback helps us improve TRADEOS.tech.</p>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-semibold text-white mb-4">Share Your Feedback</h3>

                {error && (
                  <div className="mb-4 p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-200 text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Rating */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      How would you rate your experience?
                    </label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((value) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setRating(value)}
                          className={`text-2xl p-2 rounded transition-colors ${
                            rating >= value ? 'text-yellow-400' : 'text-gray-600'
                          }`}
                        >
                          ★
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="w-full px-4 py-2 bg-glass border border-glass-border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {FEEDBACK_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Comment */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Tell us more (optional)
                    </label>
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2 bg-glass border border-glass-border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                      placeholder="What can we improve?"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting || rating === 0}
                    className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-600 text-white font-medium rounded-lg transition-colors"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackButton;
